import { useSchedule } from '../context/ScheduleContext';
import type { PendingEnrollment, GroupConflict } from './groupTypes';

// ─── Conflict warning ──────────────────────────────────────────────────────

function ConflictWarning({ conflicts, severity }: { conflicts: GroupConflict[]; severity: 'soft' | 'hard' }) {
  const isHard = severity === 'hard';
  const color = isHard ? '#dc2626' : '#d97706';
  const bg = isHard ? '#fef2f2' : '#fffbeb';
  const border = isHard ? '#fecaca' : '#fde68a';

  return (
    <div
      style={{
        margin: '0 0 12px',
        padding: '10px 12px',
        background: bg,
        border: `1px solid ${border}`,
        borderRadius: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <WarningIcon color={color} />
        <span style={{ fontSize: 12, fontWeight: 700, color }}>
          {isHard ? 'Hard Conflict Detected' : 'Scheduling Conflict'}
        </span>
      </div>
      <p style={{ margin: '0 0 8px', fontSize: 11, color: '#475569', lineHeight: 1.5 }}>
        {isHard
          ? 'This patient already has an appointment that directly overlaps this group session.'
          : 'This patient has a nearby appointment that may conflict with this group session.'}
      </p>
      {conflicts.map((c) => (
        <div
          key={c.eventId}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '4px 8px',
            background: '#fff',
            borderRadius: 4,
            marginBottom: 4,
            fontSize: 11,
          }}
        >
          <span style={{ fontWeight: 600, color: '#1e293b' }}>{c.eventTitle}</span>
          <span style={{ color: '#64748b', whiteSpace: 'nowrap', marginLeft: 8 }}>
            {formatConflictTime(c.start, c.end)}
          </span>
        </div>
      ))}
      {!isHard && (
        <p style={{ margin: '6px 0 0', fontSize: 10, color: '#92400e' }}>
          You can still enroll — the host will be notified of the overlap.
        </p>
      )}
    </div>
  );
}

function formatConflictTime(start: string, end: string): string {
  if (!start) return '';
  const s = new Date(start);
  const e = new Date(end);
  const fmt = (d: Date) =>
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${fmt(s)} – ${fmt(e)}`;
}

function formatSessionTime(start: string, end: string): string {
  if (!start) return '';
  const s = new Date(start);
  const e = new Date(end);
  const date = s.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const fmt = (d: Date) =>
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${date}  ·  ${fmt(s)} – ${fmt(e)}`;
}

// ─── Spinner ───────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <svg
      width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round"
      style={{ animation: 'group-spin 0.7s linear infinite', flexShrink: 0 }}
      aria-hidden="true"
    >
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  );
}

// ─── Outcome view ──────────────────────────────────────────────────────────

function OutcomeView({
  enrollment,
  onClose,
}: {
  enrollment: PendingEnrollment;
  onClose: () => void;
}) {
  const outcome = enrollment.outcome!;
  const success = outcome.outcome !== 'rejected';
  const color = outcome.outcome === 'enrolled' ? '#16a34a' : outcome.outcome === 'waitlisted' ? '#d97706' : '#dc2626';

  return (
    <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>
        {outcome.outcome === 'enrolled' ? '✓' : outcome.outcome === 'waitlisted' ? '⏳' : '✕'}
      </div>
      <div style={{ fontSize: 14, fontWeight: 700, color, marginBottom: 4 }}>
        {outcome.outcome === 'enrolled'
          ? 'Successfully Enrolled'
          : outcome.outcome === 'waitlisted'
            ? `Added to Waitlist (#${outcome.waitlistPosition ?? '?'})`
            : 'Enrollment Rejected'}
      </div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16, lineHeight: 1.5 }}>
        {success
          ? `${enrollment.displayLabel} has been ${outcome.outcome === 'enrolled' ? 'enrolled in' : 'waitlisted for'} ${enrollment.groupName}.`
          : outcome.rejectionReason ?? 'The host rejected this enrollment request.'}
      </div>
      <button
        onClick={onClose}
        style={{
          padding: '8px 20px', fontSize: 12, fontWeight: 600,
          color: '#fff', background: '#2563eb', border: 'none',
          borderRadius: 6, cursor: 'pointer',
        }}
      >
        Done
      </button>
    </div>
  );
}

// ─── Icons ─────────────────────────────────────────────────────────────────

function WarningIcon({ color }: { color: string }) {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

// ─── Main dialog ───────────────────────────────────────────────────────────

export function GroupEnrollDialog() {
  const { groupTherapyHandle } = useSchedule();
  const { pendingEnrollment, confirmEnrollment, cancelEnrollment } = groupTherapyHandle;

  if (!pendingEnrollment) return null;

  const { dialogState, conflictResult, outcome } = pendingEnrollment;
  const isCheckingConflict = dialogState === 'checking_conflict';
  const isEnrolling = dialogState === 'enrolling';
  const isAwaitingConfirm = dialogState === 'awaiting_confirm';
  const isDone = dialogState === 'done';
  const hasConflict = !!conflictResult?.hasConflict;
  const isHardConflict = conflictResult?.severity === 'hard';
  const canConfirm = isAwaitingConfirm && !isHardConflict;

  return (
    <>
      {/* Keyframe for spinner */}
      <style>{`@keyframes group-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* Backdrop */}
      <div
        onClick={!isEnrolling ? cancelEnrollment : undefined}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
          zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {/* Dialog */}
        <div
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label="Group Enrollment"
          style={{
            background: '#fff',
            borderRadius: 10,
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            width: 420,
            maxWidth: '90vw',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 16px 12px', borderBottom: '1px solid #e2e8f0',
              background: '#f8fafc', flexShrink: 0,
            }}
          >
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>
                Enroll in Group Session
              </div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>
                {pendingEnrollment.groupName}
              </div>
            </div>
            {!isEnrolling && (
              <button
                onClick={cancelEnrollment}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 18, lineHeight: 1, padding: '2px 4px', borderRadius: 4 }}
                aria-label="Close"
              >
                ×
              </button>
            )}
          </div>

          {/* Body */}
          <div style={{ padding: '16px', flex: 1, overflow: 'auto' }}>
            {isDone && outcome ? (
              <OutcomeView enrollment={pendingEnrollment} onClose={cancelEnrollment} />
            ) : (
              <>
                {/* Session info */}
                <div
                  style={{
                    padding: '8px 12px', marginBottom: 12, background: '#f8fafc',
                    border: '1px solid #e2e8f0', borderRadius: 6,
                  }}
                >
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Session time</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>
                    {formatSessionTime(pendingEnrollment.start, pendingEnrollment.end)}
                  </div>
                </div>

                {/* Patient */}
                <div
                  style={{
                    padding: '8px 12px', marginBottom: 12, background: '#f8fafc',
                    border: '1px solid #e2e8f0', borderRadius: 6,
                  }}
                >
                  <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>Patient</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>
                    {pendingEnrollment.displayLabel}
                  </div>
                </div>

                {/* Conflict check spinner */}
                {isCheckingConflict && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', color: '#64748b', fontSize: 12 }}>
                    <Spinner />
                    Checking for scheduling conflicts…
                  </div>
                )}

                {/* Conflict warning */}
                {hasConflict && conflictResult && !isCheckingConflict && (
                  <ConflictWarning
                    conflicts={conflictResult.conflicts}
                    severity={conflictResult.severity as 'soft' | 'hard'}
                  />
                )}

                {/* No conflict confirmation */}
                {!hasConflict && isAwaitingConfirm && (
                  <div
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px',
                      background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6,
                      marginBottom: 12, fontSize: 11, color: '#15803d',
                    }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    No scheduling conflicts found.
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          {!isDone && (
            <div
              style={{
                display: 'flex', gap: 8, padding: '12px 16px', borderTop: '1px solid #e2e8f0',
                background: '#f8fafc', flexShrink: 0, justifyContent: 'flex-end',
              }}
            >
              <button
                onClick={cancelEnrollment}
                disabled={isEnrolling}
                style={{
                  padding: '7px 16px', fontSize: 12, fontWeight: 500, border: '1px solid #e2e8f0',
                  borderRadius: 6, background: '#fff', color: '#475569',
                  cursor: isEnrolling ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmEnrollment}
                disabled={!canConfirm || isEnrolling}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '7px 16px', fontSize: 12, fontWeight: 600,
                  border: 'none', borderRadius: 6,
                  background: canConfirm ? '#2563eb' : '#cbd5e1',
                  color: canConfirm ? '#fff' : '#94a3b8',
                  cursor: canConfirm ? 'pointer' : 'not-allowed',
                }}
              >
                {isEnrolling && <Spinner />}
                {isHardConflict
                  ? 'Cannot Enroll'
                  : isEnrolling
                    ? 'Enrolling…'
                    : 'Confirm Enrollment'}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Mount helper (used in ScheduleContext) ────────────────────────────────

export function GroupEnrollDialogMount() {
  const { groupTherapyHandle } = useSchedule();
  if (!groupTherapyHandle.enabled || !groupTherapyHandle.pendingEnrollment) return null;
  return <GroupEnrollDialog />;
}
